import useSWR from 'swr';
import { fetcher } from '../lib/api';
import type {
  PlatformPerfSummaryResponse,
  PlatformPerfTimeseriesResponse,
  PlatformPerfPerRequestResponse,
  PlatformPerfByClassifierCountResponse,
  PlatformPerfByClassifierResponse,
  PlatformPerfHedgeResponse,
  PlatformPerfTopSlowResponse,
  PlatformPerfLookupResponse,
  PlatformPerfRequestDetail,
} from '../types';

const POLL_INTERVAL = 30000;

export interface PlatformPerfParams {
  from: string;
  to: string;
  orgId?: string;
  userId?: string;
}

function buildQuery(p: PlatformPerfParams & { granularity?: string; limit?: number }) {
  const qs = new URLSearchParams();
  qs.set('from', p.from);
  qs.set('to', p.to);
  if (p.orgId) qs.set('orgId', p.orgId);
  if (p.userId) qs.set('userId', p.userId);
  if (p.granularity) qs.set('granularity', p.granularity);
  if (p.limit) qs.set('limit', String(p.limit));
  return qs.toString();
}

export function usePlatformPerfSummary(params: PlatformPerfParams | null) {
  const url = params ? `/api/stats/platform-perf/summary?${buildQuery(params)}` : null;
  const { data, error, isLoading, mutate } = useSWR<PlatformPerfSummaryResponse>(url, fetcher, {
    refreshInterval: POLL_INTERVAL,
    revalidateOnFocus: false,
  });
  return { data: data ?? null, error, isLoading, refresh: () => mutate() };
}

export function usePlatformPerfTimeseries(
  params: (PlatformPerfParams & { granularity?: 'auto' | 'hour' | 'day' }) | null,
) {
  const url = params ? `/api/stats/platform-perf/timeseries?${buildQuery(params)}` : null;
  const { data, error, isLoading, mutate } = useSWR<PlatformPerfTimeseriesResponse>(url, fetcher, {
    refreshInterval: POLL_INTERVAL,
    revalidateOnFocus: false,
  });
  return { data: data ?? null, error, isLoading, refresh: () => mutate() };
}

export function usePlatformPerfPerRequest(params: PlatformPerfParams | null) {
  const url = params ? `/api/stats/platform-perf/per-request?${buildQuery(params)}` : null;
  const { data, error, isLoading, mutate } = useSWR<PlatformPerfPerRequestResponse>(url, fetcher, {
    refreshInterval: POLL_INTERVAL,
    revalidateOnFocus: false,
  });
  return { data: data ?? null, error, isLoading, refresh: () => mutate() };
}

export function usePlatformPerfByClassifierCount(params: PlatformPerfParams | null) {
  const url = params ? `/api/stats/platform-perf/by-classifier-count?${buildQuery(params)}` : null;
  const { data, error, isLoading, mutate } = useSWR<PlatformPerfByClassifierCountResponse>(url, fetcher, {
    refreshInterval: POLL_INTERVAL,
    revalidateOnFocus: false,
  });
  return { data: data ?? null, error, isLoading, refresh: () => mutate() };
}

export function usePlatformPerfByClassifier(params: PlatformPerfParams | null) {
  const url = params ? `/api/stats/platform-perf/by-classifier?${buildQuery(params)}` : null;
  const { data, error, isLoading, mutate } = useSWR<PlatformPerfByClassifierResponse>(url, fetcher, {
    refreshInterval: POLL_INTERVAL,
    revalidateOnFocus: false,
  });
  return { data: data ?? null, error, isLoading, refresh: () => mutate() };
}

export function usePlatformPerfHedge(params: PlatformPerfParams | null) {
  const url = params ? `/api/stats/platform-perf/hedge?${buildQuery(params)}` : null;
  const { data, error, isLoading, mutate } = useSWR<PlatformPerfHedgeResponse>(url, fetcher, {
    refreshInterval: POLL_INTERVAL,
    revalidateOnFocus: false,
  });
  return { data: data ?? null, error, isLoading, refresh: () => mutate() };
}

export function usePlatformPerfTopSlow(params: (PlatformPerfParams & { limit?: number }) | null) {
  const url = params ? `/api/stats/platform-perf/top-slow?${buildQuery(params)}` : null;
  const { data, error, isLoading, mutate } = useSWR<PlatformPerfTopSlowResponse>(url, fetcher, {
    refreshInterval: POLL_INTERVAL,
    revalidateOnFocus: false,
  });
  return { data: data ?? null, error, isLoading, refresh: () => mutate() };
}

export function usePlatformPerfLookup(org: string | undefined, user: string | undefined) {
  const qs = new URLSearchParams();
  if (org) qs.set('org', org);
  if (user) qs.set('user', user);
  const url = qs.toString() ? `/api/stats/platform-perf/lookup?${qs.toString()}` : null;
  const { data, error, isLoading } = useSWR<PlatformPerfLookupResponse>(url, fetcher, {
    revalidateOnFocus: false,
    dedupingInterval: 5000,
  });
  return { data: data ?? null, error, isLoading };
}

export function usePlatformPerfRequestDetail(reflectionId: string | null) {
  const url = reflectionId
    ? `/api/stats/platform-perf/request/${encodeURIComponent(reflectionId)}`
    : null;
  const { data, error, isLoading, mutate } = useSWR<PlatformPerfRequestDetail>(url, fetcher, {
    revalidateOnFocus: false,
  });
  return { data: data ?? null, error, isLoading, refresh: () => mutate() };
}
